import React, {useEffect, useState} from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {
	Add as AddIcon,
	Delete as DeleteIcon,
	Edit as EditIcon,
	FileCopy as FileCopyIcon,
	Search as SearchIcon,
} from "@material-ui/icons";
import {useHistory} from "react-router";
import API from "../../../network/dashboardAPI";
import useAlert from "../../../services/useAlert";

const PAGE_TYPE = "selection";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
		backgroundColor: "#f9fafc",
		minHeight: "100vh",
	},
	header: {
		background: "linear-gradient(135deg, #8bc34a 0%, #689f38 100%)",
		borderRadius: 12,
		padding: theme.spacing(3, 4),
		marginBottom: theme.spacing(3),
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		color: "#fff",
		boxShadow: "0 6px 20px rgba(104,159,56,0.25)",
	},
	headerTitle: {
		fontWeight: 700,
		marginBottom: 4,
	},
	headerSubtitle: {
		opacity: 0.9,
	},
	newButton: {
		backgroundColor: "#fff",
		color: "#558b2f",
		fontWeight: 600,
		borderRadius: 8,
		"&:hover": {backgroundColor: "#f1f8e9"},
	},
	statsRow: {
		display: "flex",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(3),
	},
	statCard: {
		flex: 1,
		borderRadius: 12,
		padding: theme.spacing(2, 3),
		boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
		borderLeft: "4px solid #bdbdbd",
	},
	statCardTotal: {borderLeftColor: "#37474f"},
	statCardActive: {borderLeftColor: "#43a047"},
	statCardInactive: {borderLeftColor: "#e53935"},
	statValue: {
		fontWeight: 700,
		fontSize: 32,
		lineHeight: 1,
	},
	statLabel: {
		color: "#90a4ae",
		fontWeight: 600,
		letterSpacing: 0.5,
		fontSize: 12,
		marginTop: 6,
	},
	paper: {
		borderRadius: 12,
		overflow: "hidden",
		boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
	},
	toolbar: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: theme.spacing(2, 3),
		borderBottom: "1px solid #e0e0e0",
	},
	searchInput: {
		width: 320,
		"& .MuiOutlinedInput-root": {borderRadius: 8},
	},
	tableHead: {backgroundColor: "#f4f6f8"},
	tableCell: {fontWeight: 600, color: "#37474f"},
	pageIdChip: {
		fontWeight: 600,
		color: "#1565c0",
		backgroundColor: "#e3f2fd",
	},
	loadingBox: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: theme.spacing(5),
	},
	emptyBox: {
		textAlign: "center",
		padding: theme.spacing(5),
		color: "#757575",
	},
}));

export default function Selections() {
	const classes = useStyles();
	const history = useHistory();
	const alert = useAlert();

	const [list, setList] = useState([]);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [loading, setLoading] = useState(false);

	const [deleteTarget, setDeleteTarget] = useState(null);

	useEffect(() => {
		fetchSelections();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, rowsPerPage, search]);

	const fetchSelections = async () => {
		try {
			setLoading(true);
			const response = await API.adminGetProductsSelection(
				PAGE_TYPE,
				page,
				rowsPerPage,
				search,
				"all"
			);
			const res = await response.json();
			if (res.success) {
				setList(res.data?.data?.data || []);
				setTotal(res.data?.data?.total || 0);
			} else {
				setList([]);
				alert.error("Failed to Load", res.message || "Unable to fetch selections");
			}
		} catch (err) {
			console.error("Error fetching selections:", err);
			alert.error("Network Error", "Failed to load selections. Please check your connection.");
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (e) => {
		setSearch(e.target.value);
		setPage(0);
	};

	const handleCreate = () => history.push("/admin/selections/new");
	const handleEdit = (item) => history.push(`/admin/selections/${item.page_id}/edit`);

	const handleCopyId = async (pageId) => {
		const text = String(pageId);
		try {
			if (navigator.clipboard && navigator.clipboard.writeText) {
				await navigator.clipboard.writeText(text);
			} else {
				const ta = document.createElement("textarea");
				ta.value = text;
				document.body.appendChild(ta);
				ta.select();
				document.execCommand("copy");
				document.body.removeChild(ta);
			}
			alert.toast(`Copied page_id ${text} to clipboard`, "success", 1500);
		} catch (err) {
			console.error("Copy failed:", err);
			alert.error("Copy Failed", "Could not copy page_id to clipboard");
		}
	};

	const handleToggleStatus = async (item) => {
		const newStatus = item.status === 1 ? 0 : 1;
		try {
			const response = await API.adminUpdatePagesStatus(item.page_id, newStatus, {type: "status"});
			const res = await response.json();
			if (res.success) {
				fetchSelections();
			} else {
				alert.error("Update Failed", res.message || "Failed to update status");
			}
		} catch (err) {
			console.error("Error toggling status:", err);
			alert.error("Update Failed", "Failed to update status");
		}
	};

	const handleDeleteConfirm = async () => {
		if (!deleteTarget) return;
		try {
			const response = await API.adminDeletePagesSelection(deleteTarget.page_id);
			const res = await response.json();
			if (res.success) {
				alert.toast("Selection deleted successfully", "success", 2000);
				fetchSelections();
			} else {
				alert.error("Delete Failed", res.message || "Failed to delete selection");
			}
		} catch (err) {
			console.error("Error deleting selection:", err);
			alert.error("Delete Failed", "Failed to delete selection");
		} finally {
			setDeleteTarget(null);
		}
	};

	const activeCount = list.filter((i) => i.status === 1).length;
	const inactiveCount = list.filter((i) => i.status !== 1).length;

	return (
		<div className={classes.root}>
			{/* Header */}
			<Box className={classes.header}>
				<div>
					<Typography variant="h5" className={classes.headerTitle}>
						Product Selections
					</Typography>
					<Typography variant="body2" className={classes.headerSubtitle}>
						Reusable condition presets — copy the <strong>page_id</strong> into Strapi CMS
					</Typography>
				</div>
				<Button
					variant="contained"
					className={classes.newButton}
					startIcon={<AddIcon/>}
					onClick={handleCreate}
				>
					New Selection
				</Button>
			</Box>

			{/* Stats */}
			<Box className={classes.statsRow}>
				<Paper className={`${classes.statCard} ${classes.statCardTotal}`}>
					<Typography className={classes.statValue}>{total}</Typography>
					<Typography className={classes.statLabel}>TOTAL SELECTIONS</Typography>
				</Paper>
				<Paper className={`${classes.statCard} ${classes.statCardActive}`}>
					<Typography className={classes.statValue} style={{color: "#43a047"}}>
						{activeCount}
					</Typography>
					<Typography className={classes.statLabel}>ACTIVE</Typography>
				</Paper>
				<Paper className={`${classes.statCard} ${classes.statCardInactive}`}>
					<Typography className={classes.statValue} style={{color: "#e53935"}}>
						{inactiveCount}
					</Typography>
					<Typography className={classes.statLabel}>INACTIVE</Typography>
				</Paper>
			</Box>

			{/* Table */}
			<Paper className={classes.paper}>
				<Box className={classes.toolbar}>
					<Typography variant="h6" style={{fontWeight: 600}}>
						All Selections
					</Typography>
					<TextField
						className={classes.searchInput}
						placeholder="Search by name or ID..."
						variant="outlined"
						size="small"
						value={search}
						onChange={handleSearch}
						InputProps={{
							startAdornment: <SearchIcon color="action" style={{marginRight: 8}}/>,
						}}
					/>
				</Box>

				{loading ? (
					<Box className={classes.loadingBox}>
						<CircularProgress/>
					</Box>
				) : list.length === 0 ? (
					<Box className={classes.emptyBox}>
						<Typography variant="body1" gutterBottom>
							No selections found
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							startIcon={<AddIcon/>}
							onClick={handleCreate}
							style={{marginTop: 16}}
						>
							Create Your First Selection
						</Button>
					</Box>
				) : (
					<>
						<TableContainer>
							<Table>
								<TableHead className={classes.tableHead}>
									<TableRow>
										<TableCell className={classes.tableCell} style={{width: 120}}>
											PAGE ID
										</TableCell>
										<TableCell className={classes.tableCell}>NAME</TableCell>
										<TableCell className={classes.tableCell} align="center" style={{width: 140}}>
											STATUS
										</TableCell>
										<TableCell className={classes.tableCell} align="center" style={{width: 120}}>
											ACTIONS
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{list.map((item) => (
										<TableRow key={item.page_id} hover>
											<TableCell>
												<Box style={{display: "flex", alignItems: "center", gap: 4}}>
													<Chip size="small" label={`# ${item.page_id}`} className={classes.pageIdChip}/>
													<Tooltip title="Copy page_id">
														<IconButton size="small" onClick={() => handleCopyId(item.page_id)}>
															<FileCopyIcon style={{fontSize: 16, color: "#689f38"}}/>
														</IconButton>
													</Tooltip>
												</Box>
											</TableCell>
											<TableCell>
												<Typography variant="body2" style={{fontWeight: 500}}>
													{item.title}
												</Typography>
											</TableCell>
											<TableCell align="center">
												<Tooltip title="Toggle status">
													<Chip
														size="small"
														clickable
														onClick={() => handleToggleStatus(item)}
														label={item.status === 1 ? "Active" : "Inactive"}
														color={item.status === 1 ? "primary" : "default"}
														style={{fontWeight: 600}}
													/>
												</Tooltip>
											</TableCell>
											<TableCell align="center">
												<IconButton size="small" color="primary" title="Edit" onClick={() => handleEdit(item)}>
													<EditIcon fontSize="small"/>
												</IconButton>
												<IconButton
													size="small"
													color="secondary"
													title="Delete"
													onClick={() => setDeleteTarget(item)}
												>
													<DeleteIcon fontSize="small"/>
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						<TablePagination
							rowsPerPageOptions={[10, 25, 50]}
							component="div"
							count={total}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={(e, newPage) => setPage(newPage)}
							onRowsPerPageChange={(e) => {
								setRowsPerPage(parseInt(e.target.value, 10));
								setPage(0);
							}}
						/>
					</>
				)}
			</Paper>

			{/* Delete confirm */}
			<Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
				<DialogTitle>Confirm Delete</DialogTitle>
				<DialogContent>
					<Typography>Are you sure you want to delete this selection?</Typography>
					{deleteTarget && (
						<Typography variant="body2" color="textSecondary" style={{marginTop: 8}}>
							<strong>#{deleteTarget.page_id} — {deleteTarget.title}</strong>
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteTarget(null)} color="default">
						Cancel
					</Button>
					<Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
